'use client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { A11yOptions, AutoplayOptions } from 'swiper/types';
import { JSX, RefObject, useRef } from 'react';
import Button from '../form-elements/button/Button';
import ArrowDown from '../svg/ArrowDown';
import style from './swiperCarousel.module.scss';

interface Breakpoint {
  slidesPerView: number;
  spaceBetween?: number;
  freeMode?: { enabled: boolean, sticky: boolean };
}

interface Props {
  centeredSlides?: boolean;
  centerInsufficientSlides?: boolean;
  initialSlide?: number;
  sliderClassName?: string;
  slideClassName?: string;
  sliderId?: string;
  singleDotClassName?: string;
  activeDotClassName?: string;
  pagContainerClassName?: string;
  prevBtnClassName?: string[];
  nextBtnClassName?: string[];
  dotsClassName?: string;
  children: JSX.Element[];
  autoplay?: boolean;
  autoplayOptions?: AutoplayOptions;
  dots?: boolean;
  nav?: boolean;
  scrollbar?: boolean;
  navIds?: {
    next: number;
    prev: number;
  };
  arrowColors?:string;
  loop?: boolean;
  slidesPerView?: number;
  freeMode?: boolean;
  breakpoints?: Record<number, Breakpoint>;
  gridRows?: number;
  spaceBetween?: number;
  ref?: RefObject<any>;
  speed?:number,
  a11y?:A11yOptions
}

const SwiperCarousel = ({
  centeredSlides = false,
  centerInsufficientSlides = false,
  initialSlide = 0,
  sliderClassName,
  slideClassName,
  sliderId,
  singleDotClassName,
  activeDotClassName,
  dotsClassName,
  pagContainerClassName,
  prevBtnClassName = [],
  nextBtnClassName = [],
  children,
  autoplay,
  autoplayOptions = {
    delay: 5000,
    pauseOnMouseEnter: true,
    disableOnInteraction: true,
  },
  arrowColors = '#000',
  dots = true,
  nav = true,
  scrollbar = false,
  navIds,
  loop = false,
  slidesPerView = 1,
  freeMode,
  breakpoints,
  gridRows,
  spaceBetween = 16,
  ref,
  speed,
  a11y
}: Props) => {

  return (
    <div style={{position: 'relative'}}>
      <Swiper
        ref={ref}
        modules={[Navigation, Pagination, Autoplay, Scrollbar]}
        className={sliderClassName}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        freeMode={{ enabled: freeMode, sticky: freeMode }}
        watchSlidesProgress
        centeredSlides={centeredSlides}
        initialSlide={initialSlide}
        centerInsufficientSlides={centerInsufficientSlides}
        a11y={a11y}
        id={sliderId}
        scrollbar={
          scrollbar ?
            {
              enabled: true,
              draggable: true,
              dragSize: 24,
              snapOnRelease: true,
              hide:false
              // el: ".swiper-scrollbar"
            }
            :
            false
        }
        speed={speed}
        mousewheel={scrollbar}
        pagination={
          dots
            ? {
              clickable: true,
              ...(singleDotClassName && { bulletClass: singleDotClassName }),
              ...(activeDotClassName && { bulletActiveClass: activeDotClassName }),
              ...(dotsClassName && { horizontalClass: dotsClassName }),
              ...(pagContainerClassName && { horizontalClass: pagContainerClassName }),
              renderBullet(index, className) {
                const classes = [singleDotClassName, className].filter(Boolean).join(' ');
                return `<button class="${classes}"><span></span></button>`;
              },
              enabled: true,
            }
            : false
        }
        navigation={
          nav && navIds
            ? {
              prevEl: `#prev-carousel-swiper-${navIds.prev}`,
              nextEl: `#next-carousel-swiper-${navIds.next}`,
              enabled: true,
            }
            : false
        }
        autoplay={autoplay ? autoplayOptions : false}
        loop={loop}
        breakpoints={breakpoints}
        grid={{ rows: gridRows, fill: 'column' }}
      >
        {
          children.map((child, index) =>
            <SwiperSlide className={slideClassName} key={index} style={{height: 'auto'}}>
              {child}
            </SwiperSlide>
          )
        }
      </Swiper>
      {nav && navIds
        ?
        <>
          <Button id={`prev-carousel-swiper-${navIds.prev}`} classes={[style.btn, style.prev, ...prevBtnClassName]} label="arrow-left">
            <ArrowDown hexColor={arrowColors} width={33} height={33} slimmer />
          </Button>
          <Button id={`next-carousel-swiper-${navIds.next}`} classes={[style.btn, style.next, ...nextBtnClassName]} label="arrow-right">
            <ArrowDown hexColor={arrowColors} width={33} height={33} slimmer />
          </Button>
        </>
        : <></>
      }
    </div>
  );
};

export default SwiperCarousel;
